import { useEffect, useRef, useState } from 'react'

export interface Size {
  width: number
  height: number
}

/**
 * Element size via ResizeObserver.
 *
 * Charts need a pixel width and the grid is fluid. This is a dozen lines and
 * avoids pulling in @visx/responsive for a single measurement, which also keeps
 * the render path free of the extra wrapper component that ParentSize adds.
 */
export function useElementSize<T extends HTMLElement>(): [
  React.RefObject<T | null>,
  Size,
] {
  const ref = useRef<T>(null)
  const [size, setSize] = useState<Size>({ width: 0, height: 0 })

  useEffect(() => {
    const element = ref.current
    if (element === null) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry === undefined) return
      const { width, height } = entry.contentRect
      setSize((previous) =>
        // Sub-pixel churn from a fluid grid would re-render the chart forever.
        Math.abs(previous.width - width) < 1 && Math.abs(previous.height - height) < 1
          ? previous
          : { width, height },
      )
    })

    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return [ref, size]
}
